import React, { useState, useEffect, useCallback } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { mileageAPI, MileageEntry, MileageEntryCreate } from '../../services/api';
import { useProjectStore } from '../../store/projectStore';
import { useClientStore } from '../../store/clientStore';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';

const inputClasses =
  'w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent';

export const MileageSection: React.FC = () => {
  const { projects, fetchProjects } = useProjectStore();
  const { clients, fetchClients } = useClientStore();

  const [entries, setEntries] = useState<MileageEntry[]>([]);
  const [irsRate, setIrsRate] = useState<number>(0);
  const [irsYear, setIrsYear] = useState<number>(new Date().getFullYear());
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<{
    project_id: string;
    date: string;
    start_location: string;
    end_location: string;
    distance_miles: string;
    is_round_trip: boolean;
    description: string;
  }>({
    project_id: '',
    date: new Date().toISOString().split('T')[0],
    start_location: '',
    end_location: '',
    distance_miles: '',
    is_round_trip: false,
    description: '',
  });

  const fetchEntries = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await mileageAPI.list();
      setEntries(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch mileage entries');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchRate = useCallback(async () => {
    try {
      const rateData = await mileageAPI.getRate();
      setIrsRate(rateData.rate_per_mile);
      setIrsYear(rateData.year);
    } catch (err: any) {
      // Rate fetch failure is non-critical
    }
  }, []);

  useEffect(() => {
    fetchEntries();
    fetchRate();
    fetchProjects();
    fetchClients();
  }, [fetchEntries, fetchRate, fetchProjects, fetchClients]);

  // Group projects by client for the dropdown
  const groupedProjects = clients.map(client => ({
    client,
    projects: projects.filter(p => p.client_id === client.id && p.is_active),
  })).filter(group => group.projects.length > 0);

  const distanceNum = parseFloat(formData.distance_miles) || 0;
  const effectiveDistance = formData.is_round_trip ? distanceNum * 2 : distanceNum;
  const calculatedAmount = effectiveDistance * irsRate;

  const resetForm = () => {
    setFormData({
      project_id: '',
      date: new Date().toISOString().split('T')[0],
      start_location: '',
      end_location: '',
      distance_miles: '',
      is_round_trip: false,
      description: '',
    });
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const payload: MileageEntryCreate = {
        project_id: formData.project_id,
        date: formData.date,
        start_location: formData.start_location,
        end_location: formData.end_location,
        distance_miles: distanceNum,
        rate_per_mile: irsRate,
        is_round_trip: formData.is_round_trip,
        description: formData.description || undefined,
      };
      await mileageAPI.create(payload);
      await fetchEntries();
      resetForm();
      setIsModalOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create mileage entry');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await mileageAPI.delete(id);
      setEntries(prev => prev.filter(entry => entry.id !== id));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete mileage entry');
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Mileage Entries
          </h2>
          {irsRate > 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {irsYear} IRS Standard Mileage Rate: ${irsRate.toFixed(3)}/mile
            </p>
          )}
        </div>
        <Button variant="primary" onClick={() => setIsModalOpen(true)}>
          <PlusIcon className="h-5 w-5 mr-1" />
          Add Mileage
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg text-red-700 dark:text-red-400 text-sm">
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 underline hover:no-underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          Loading mileage entries...
        </div>
      )}

      {/* Empty state */}
      {!isLoading && entries.length === 0 && (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <p className="text-gray-500 dark:text-gray-400">
            No mileage entries yet. Click "Add Mileage" to create one.
          </p>
        </div>
      )}

      {/* Entries list */}
      {!isLoading && entries.length > 0 && (
        <div className="space-y-3">
          {entries.map(entry => (
            <div
              key={entry.id}
              className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-gray-900 dark:text-white">
                    {entry.start_location} &rarr; {entry.end_location}
                  </span>
                  {entry.is_round_trip && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                      Round Trip
                    </span>
                  )}
                </div>
                <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {entry.distance_miles} miles
                  {entry.is_round_trip ? ` (${entry.distance_miles * 2} mi total)` : ''}
                  {' '}&middot;{' '}
                  ${entry.rate_per_mile.toFixed(3)}/mi
                  {' '}&middot;{' '}
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    ${entry.calculated_amount.toFixed(2)}
                  </span>
                </div>
              </div>
              <button
                onClick={() => handleDelete(entry.id)}
                className="ml-4 p-2 text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 transition-colors"
                title="Delete mileage entry"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        title="Add Mileage Entry"
        size="lg"
      >
        <form onSubmit={handleCreate} className="space-y-4">
          {/* Project */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Project
            </label>
            <select
              required
              value={formData.project_id}
              onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
              className={inputClasses}
            >
              <option value="">Select a project...</option>
              {groupedProjects.map((group) => (
                <optgroup key={group.client.id} label={group.client.name}>
                  {group.projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Date
            </label>
            <input
              type="date"
              required
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className={inputClasses}
            />
          </div>

          {/* Start Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Start Location
            </label>
            <input
              type="text"
              required
              placeholder="e.g., Home Office"
              value={formData.start_location}
              onChange={(e) => setFormData({ ...formData, start_location: e.target.value })}
              className={inputClasses}
            />
          </div>

          {/* End Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              End Location
            </label>
            <input
              type="text"
              required
              placeholder="e.g., Client Office"
              value={formData.end_location}
              onChange={(e) => setFormData({ ...formData, end_location: e.target.value })}
              className={inputClasses}
            />
          </div>

          {/* Distance */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Distance (miles)
            </label>
            <input
              type="number"
              required
              min="0.1"
              step="0.1"
              placeholder="0.0"
              value={formData.distance_miles}
              onChange={(e) => setFormData({ ...formData, distance_miles: e.target.value })}
              className={inputClasses}
            />
          </div>

          {/* Round Trip Toggle */}
          <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_round_trip}
                onChange={(e) => setFormData({ ...formData, is_round_trip: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 dark:bg-gray-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-green-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600" />
            </label>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Round Trip
            </span>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description (optional)
            </label>
            <input
              type="text"
              placeholder="Purpose of trip..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className={inputClasses}
            />
          </div>

          {/* Calculated Amount */}
          {distanceNum > 0 && irsRate > 0 && (
            <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="text-sm text-green-700 dark:text-green-400">
                <span className="font-medium">Estimated Reimbursement:</span>{' '}
                ${calculatedAmount.toFixed(2)}
                <span className="text-green-600 dark:text-green-500 ml-1">
                  ({effectiveDistance.toFixed(1)} mi x ${irsRate.toFixed(3)}/mi)
                </span>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setIsModalOpen(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" isLoading={isSubmitting}>
              Create Entry
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

